// frontend/modules/customer-category/components/CustomerCategoryLocationRequirements.tsx
import React, { useState, useEffect } from 'react';
import {
    Table,
    Button,
    Space,
    Switch,
    Input,
    InputNumber,
    Select,
    Modal,
    Form,
    message,
    Tag,
    Tooltip,
    Popconfirm
} from 'antd';
import {
    PlusOutlined,
    EditOutlined,
    DeleteOutlined,
    InfoCircleOutlined,
    HomeOutlined
} from '@ant-design/icons';
import { useTheme } from '@/components/common/ThemeProvider';
import { useLocationConfig } from '@/modules/location/hooks/useLocationConfig';

const { Option } = Select;
const { TextArea } = Input;

interface LocationRequirement {
    id?: number;
    locationConfigName: string;
    addressType: string;
    isRequired: boolean;
    isPrimary: boolean;
    isVisible: boolean;
    displayOrder: number;
    customLabel?: string;
    configOverrides?: string;
    description?: string;
}

interface CustomerCategoryLocationRequirementsProps {
    categoryId?: number;
    locationRequirements: LocationRequirement[];
    onLocationRequirementsChange: (requirements: LocationRequirement[]) => void;
    disabled?: boolean;
}

const addressTypes = [
    { value: 'HOME', label: 'Home', color: 'blue' },
    { value: 'WORK', label: 'Work', color: 'green' },
    { value: 'MAILING', label: 'Mailing', color: 'orange' },
    { value: 'EMERGENCY', label: 'Emergency', color: 'red' },
    { value: 'TEMPORARY', label: 'Temporary', color: 'purple' },
    { value: 'OTHER', label: 'Other', color: 'default' }
];

const CustomerCategoryLocationRequirements: React.FC<CustomerCategoryLocationRequirementsProps> = ({
    categoryId,
    locationRequirements,
    onLocationRequirementsChange,
    disabled = false
}) => {
    const { dark } = useTheme();
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [editingRequirement, setEditingRequirement] = useState<LocationRequirement | null>(null);
    const [form] = Form.useForm();
    
    // Use actual location configurations from the location management module
    const { configs: locationConfigs, isLoading: isLoadingLocationConfigs, fetchConfigsByEntityType } = useLocationConfig();
    
    // Fetch location configurations on component mount
    useEffect(() => {
        fetchConfigsByEntityType('ALL');
    }, []); // Remove function dependency to prevent infinite loops

    const handleAddRequirement = () => {
        setEditingRequirement(null);
        form.resetFields();
        form.setFieldsValue({
            isRequired: false,
            isPrimary: false,
            isVisible: true,
            displayOrder: locationRequirements.length,
            addressType: 'HOME'
        });
        setIsModalVisible(true);
    };

    const handleEditRequirement = (requirement: LocationRequirement) => {
        setEditingRequirement(requirement);
        form.setFieldsValue(requirement);
        setIsModalVisible(true);
    };

    const handleDeleteRequirement = (locationConfigName: string) => {
        const updatedRequirements = locationRequirements.filter(
            req => req.locationConfigName !== locationConfigName
        );
        onLocationRequirementsChange(updatedRequirements);
        message.success('Location requirement removed');
    };

    const handleModalOk = async () => {
        try {
            const values = await form.validateFields();
            
            const requirement: LocationRequirement = {
                ...values,
                id: editingRequirement?.id
            };

            let updatedRequirements;
            if (editingRequirement) {
                // Update existing requirement
                updatedRequirements = locationRequirements.map(req =>
                    req.locationConfigName === editingRequirement.locationConfigName ? requirement : req
                );
            } else {
                // Add new requirement
                const exists = locationRequirements.some(req => req.locationConfigName === values.locationConfigName);
                if (exists) {
                    message.error('Location configuration already added');
                    return;
                }
                updatedRequirements = [...locationRequirements, requirement];
            }

            // Ensure only one primary location
            if (values.isPrimary) {
                updatedRequirements = updatedRequirements.map(req => ({
                    ...req,
                    isPrimary: req.locationConfigName === values.locationConfigName
                }));
            }

            onLocationRequirementsChange(updatedRequirements);
            setIsModalVisible(false);
            message.success(`Location requirement ${editingRequirement ? 'updated' : 'added'} successfully`);
        } catch (error) {
            console.error('Form validation failed:', error);
        }
    };

    const handleModalCancel = () => {
        setIsModalVisible(false);
        setEditingRequirement(null);
    };

    const handlePropertyChange = (locationConfigName: string, property: string, value: any) => {
        let updatedRequirements = locationRequirements.map(req => {
            if (req.locationConfigName === locationConfigName) {
                return { ...req, [property]: value };
            }
            return req;
        });

        // Ensure only one primary location
        if (property === 'isPrimary' && value === true) {
            updatedRequirements = updatedRequirements.map(req => ({
                ...req,
                isPrimary: req.locationConfigName === locationConfigName
            }));
        }

        onLocationRequirementsChange(updatedRequirements);
    };

    const columns = [
        {
            title: 'Location Configuration',
            key: 'locationConfig',
            render: (_:any, record: LocationRequirement) => {
                const config = locationConfigs.find(c => c.configName === record.locationConfigName);
                return (
                    <div>
                        <div className="font-medium flex items-center">
                            {record.isPrimary && <HomeOutlined className="mr-1 text-blue-500" />}
                            {config?.configName || record.locationConfigName}
                        </div>
                        <div className="text-sm text-gray-500">{record.locationConfigName}</div>
                    </div>
                );
            }
        },
        {
            title: 'Address Type',
            key: 'addressType',
            render: (_:any, record: LocationRequirement) => {
                const addressType = addressTypes.find(at => at.value === record.addressType);
                return (
                    <Select
                        value={record.addressType}
                        onChange={(value) => handlePropertyChange(record.locationConfigName, 'addressType', value)}
                        disabled={disabled}
                        size="small"
                        style={{ width: '100%' }}
                    >
                        {addressTypes.map(at => (
                            <Option key={at.value} value={at.value}>
                                <Tag color={at.color}>{at.label}</Tag>
                            </Option>
                        ))}
                    </Select>
                );
            }
        },
        {
            title: 'Custom Label',
            key: 'customLabel',
            render: (_:any, record: LocationRequirement) => (
                <Input
                    value={record.customLabel}
                    onChange={(e) => handlePropertyChange(record.locationConfigName, 'customLabel', e.target.value)}
                    placeholder="Optional custom label"
                    disabled={disabled}
                    size="small"
                />
            )
        },
        {
            title: 'Required',
            key: 'isRequired',
            width: 100,
            render: (_:any, record: LocationRequirement) => (
                <Switch
                    checked={record.isRequired}
                    onChange={(checked) => handlePropertyChange(record.locationConfigName, 'isRequired', checked)}
                    disabled={disabled}
                />
            )
        },
        {
            title: 'Primary',
            key: 'isPrimary',
            width: 100,
            render: (_:any, record: LocationRequirement) => (
                <Tooltip title="Main address for this customer category">
                    <Switch
                        checked={record.isPrimary}
                        onChange={(checked) => handlePropertyChange(record.locationConfigName, 'isPrimary', checked)}
                        disabled={disabled}
                    />
                </Tooltip>
            )
        },
        {
            title: 'Visible',
            key: 'isVisible',
            width: 100,
            render: (_:any, record: LocationRequirement) => (
                <Switch
                    checked={record.isVisible}
                    onChange={(checked) => handlePropertyChange(record.locationConfigName, 'isVisible', checked)}
                    disabled={disabled}
                />
            )
        },
        {
            title: 'Order',
            key: 'displayOrder',
            width: 100,
            render: (_:any, record: LocationRequirement) => (
                <InputNumber
                    value={record.displayOrder}
                    onChange={(value) => handlePropertyChange(record.locationConfigName, 'displayOrder', value || 0)}
                    min={0}
                    disabled={disabled}
                    size="small"
                />
            )
        },
        {
            title: 'Actions',
            key: 'actions',
            width: 120,
            render: (_:any, record: LocationRequirement) => (
                <Space size="small">
                    <Tooltip title="Edit">
                        <Button
                            icon={<EditOutlined />}
                            onClick={() => handleEditRequirement(record)}
                            size="small"
                            disabled={disabled}
                        />
                    </Tooltip>
                    <Popconfirm
                        title="Remove location requirement?"
                        description="This will remove the location requirement from this category."
                        onConfirm={() => handleDeleteRequirement(record.locationConfigName)}
                        okText="Yes"
                        cancelText="No"
                        disabled={disabled}
                    >
                        <Button
                            danger
                            icon={<DeleteOutlined />}
                            size="small"
                            disabled={disabled}
                        />
                    </Popconfirm>
                </Space>
            )
        }
    ];

    const availableLocationConfigs = (locationConfigs || []).filter(
        config => !locationRequirements.some(req => req.locationConfigName === config.configName)
    );

    return (
        <div className="space-y-4">
            <div className="flex justify-between items-center">
                <div>
                    <h3 className={`text-lg font-semibold ${dark ? 'text-white' : ''}`}>
                        Location Requirements
                    </h3>
                    <p className={`text-sm ${dark ? 'text-gray-300' : 'text-gray-600'}`}>
                        Configure which address/location information is required for this customer category
                    </p>
                </div>
                {!disabled && (
                    <Button
                        type="primary"
                        icon={<PlusOutlined />}
                        onClick={handleAddRequirement}
                        loading={isLoadingLocationConfigs}
                        disabled={isLoadingLocationConfigs}
                    >
                        Add Location Requirement
                    </Button>
                )}
            </div>

            <Table
                dataSource={locationRequirements}
                columns={columns}
                rowKey="locationConfigName"
                pagination={false}
                size="middle"
                locale={{
                    emptyText: 'No location requirements configured'
                }}
            />

            <Modal
                title={`${editingRequirement ? 'Edit' : 'Add'} Location Requirement`}
                open={isModalVisible}
                onOk={handleModalOk}
                onCancel={handleModalCancel}
                width={600}
                className={dark ? 'dark' : ''}
            >
                <Form
                    form={form}
                    layout="vertical"
                    className="mt-4"
                >
                    <Form.Item
                        name="locationConfigName"
                        label="Location Configuration"
                        rules={[{ required: true, message: 'Please select a location configuration' }]}
                    >
                        <Select
                            placeholder="Select location configuration"
                            disabled={!!editingRequirement || isLoadingLocationConfigs}
                            loading={isLoadingLocationConfigs}
                            showSearch
                            optionFilterProp="children"
                        >
                            {(editingRequirement ? (locationConfigs || []) : availableLocationConfigs).map(config => (
                                <Option key={config.configName} value={config.configName}>
                                    <div>
                                        <div className="font-medium">{config.configName}</div>
                                        <div className="text-sm text-gray-500">{config.entityType} - {config.addressType}</div>
                                    </div>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="addressType"
                        label="Address Type"
                        rules={[{ required: true, message: 'Please select an address type' }]}
                    >
                        <Select placeholder="Select address type">
                            {addressTypes.map(at => (
                                <Option key={at.value} value={at.value}>
                                    <Tag color={at.color}>{at.label}</Tag>
                                </Option>
                            ))}
                        </Select>
                    </Form.Item>

                    <Form.Item
                        name="customLabel"
                        label="Custom Label"
                        extra="Optional custom label to display instead of the default configuration name"
                    >
                        <Input placeholder="e.g., Home Address, Office Location" />
                    </Form.Item>

                    <div className="grid grid-cols-3 gap-4">
                        <Form.Item
                            name="isRequired"
                            label="Required"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>

                        <Form.Item
                            name="isPrimary"
                            label="Primary"
                            valuePropName="checked"
                            extra="Main address for this category"
                        >
                            <Switch />
                        </Form.Item>

                        <Form.Item
                            name="isVisible"
                            label="Visible"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </div>

                    <Form.Item
                        name="displayOrder"
                        label="Display Order"
                        rules={[{ required: true, message: 'Please enter display order' }]}
                    >
                        <InputNumber
                            min={0}
                            style={{ width: '100%' }}
                            placeholder="0"
                        />
                    </Form.Item>

                    <Form.Item
                        name="configOverrides"
                        label="Configuration Overrides"
                        extra="Optional JSON configuration overrides for this location"
                    >
                        <TextArea
                            rows={3}
                            placeholder='{"required_fields": ["street", "city", "postal_code"], "validation": {"postal_code": "^[0-9]{5}$"}}'
                        />
                    </Form.Item>

                    <Form.Item
                        name="description"
                        label="Description"
                        extra="Optional description or help text for this location requirement"
                    >
                        <TextArea
                            rows={2}
                            placeholder="Please provide your current residential address"
                        />
                    </Form.Item>
                </Form>
            </Modal>
        </div>
    );
};

export default CustomerCategoryLocationRequirements;
